import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, ShieldCheck, MessageSquare, Send, X, PauseCircle, PlayCircle, Wrench, PhoneCall, ShoppingBag, RefreshCw, Link2, Copy } from "lucide-react";
import { toast } from "sonner";
import SignalWireInboundAudit from "@/components/SignalWireInboundAudit";
import InboundBillingAudit from "@/components/InboundBillingAudit";
import DialerSettingsCard from "@/components/DialerSettingsCard";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ds, setDs] = useState({ connected: false });
  const [tg, setTg] = useState({ configured: false, has_token: false, chat_id: null, events: [] });
  const [tgToken, setTgToken] = useState("");
  const [tgBusy, setTgBusy] = useState(false);
  const [smsStatus, setSmsStatus] = useState({ sms_paused: false, enabled: true });
  const [vmStatus, setVmStatus] = useState({ vm_paused: false });
  const [vmBusy, setVmBusy] = useState(false);
  const [reflowBusy, setReflowBusy] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // ============ AI Lead Manager (Phase 1: Retell inbound voice) ============
  // Read/write via /api/settings/ai-lead-manager. NEVER exposes the Retell
  // API key — the backend keeps it in .env and only reports whether it's
  // configured (boolean).
  const [aiLm, setAiLm] = useState({
    voice_enabled: false, sms_enabled: false,
    retell_agent_id: "", transfer_number: "",
    greeting_override: "", retell_api_key_configured: false,
  });
  const [aiLmBusy, setAiLmBusy] = useState(false);
  const loadAiLm = async () => {
    try {
      const { data } = await api.get("/settings/ai-lead-manager");
      setAiLm({ ...aiLm, ...data });
    } catch (_) { /* non-admin or no route — silent */ }
  };
  const saveAiLm = async (patch) => {
    setAiLmBusy(true);
    try {
      const { data } = await api.put("/settings/ai-lead-manager", patch);
      setAiLm({ ...aiLm, ...data });
      toast.success("AI Lead Manager settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setAiLmBusy(false); }
  };
  useEffect(() => { loadAiLm(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // ============ SignalWire number pool + auto-buy ============
  // Live view of every SignalWire number that's rotating for outbound
  // calls. The pool auto-syncs from SignalWire every 60s — this card
  // shows the current state + a one-click "buy 10 more" flow.
  const [swPool, setSwPool]           = useState({ pool_size: 0, numbers: [] });
  const [swBuyAreaCode, setSwBuyAreaCode] = useState("239");
  const [swBuyCount,   setSwBuyCount]     = useState(10);
  const [swBusy,       setSwBusy]         = useState(false);
  const loadSwPool = async () => {
    try {
      const { data } = await api.get("/dialer/caller-id-usage");
      setSwPool({ pool_size: data.pool_size || 0, numbers: data.numbers || [] });
    } catch (_) { /* non-admin — silently skip */ }
  };
  const refreshSwPool = async () => {
    setSwBusy(true);
    try {
      const { data } = await api.post("/dialer/caller-id-pool/refresh");
      toast.success(`Pool refreshed — ${data.pool_size} numbers`);
      await loadSwPool();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setSwBusy(false); }
  };
  const buySwNumbers = async () => {
    const ac = String(swBuyAreaCode || "").trim();
    if (!/^\d{3}$/.test(ac)) return toast.error("Enter a 3-digit US area code");
    const n = Number(swBuyCount);
    if (!(n >= 1 && n <= 10)) return toast.error("Count must be 1–10 per purchase (~$1/mo each)");
    if (!window.confirm(`Buy ${n} SignalWire number(s) in area code ${ac}? ~$${(n * 0.5).toFixed(2)}/mo total.`)) return;
    setSwBusy(true);
    try {
      const { data } = await api.post("/voice/signalwire/buy-numbers", {
        area_code: ac, count: n,
      });
      const okList = (data.bought || []).map((x) => x.phone).join(", ");
      const failList = (data.errors || []).map((x) => `${x.phone}: ${x.error}`).join("\n");
      if (data.count_bought > 0) {
        toast.success(`Bought ${data.count_bought} × +1${ac}: ${okList} — rotating within 60s`);
      }
      if (data.count_failed > 0) toast.error(`Some failed:\n${failList}`);
      // Force-sync so the new numbers show up immediately, not 60s later.
      try { await api.post("/dialer/caller-id-pool/refresh"); } catch (_) { /* ignore */ }
      await loadSwPool();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    } finally { setSwBusy(false); }
  };

  useEffect(() => {
    api.get("/profile")
      .then((r) => setProfile(r.data))
      .catch(() => toast.error("Could not load profile"))
      .finally(() => setLoading(false));
    api.get("/docusign/status").then((r) => setDs(r.data)).catch(() => {});
    api.get("/integrations/telegram/status").then((r) => setTg(r.data)).catch(() => {});
    api.get("/ai-sms/status").then((r) => setSmsStatus(r.data)).catch(() => {});
    api.get("/voicemail/status").then((r) => setVmStatus(r.data)).catch(() => {});
    loadSwPool();  // admins only — endpoint gates it
    const flag = searchParams.get("docusign");
    if (flag === "connected") toast.success("DocuSign connected");
    if (flag === "error") toast.error("DocuSign connect failed");
    if (flag === "invalid_state") toast.error("DocuSign session expired — try again");
    if (flag) setSearchParams({});
  }, []); // eslint-disable-line

  const connectDs = async () => {
    try {
      const { data } = await api.get("/docusign/connect");
      window.location.href = data.url;
    } catch {
      toast.error("Could not start DocuSign OAuth");
    }
  };
  const disconnectDs = async () => {
    await api.post("/docusign/disconnect");
    setDs({ connected: false });
    toast.success("DocuSign disconnected");
  };

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const num = (k, v) => set(k, v === "" ? "" : Number(v));

  // ============ Telegram ============
  const setupTelegram = async () => {
    if (!tgToken.trim() || !tgToken.includes(":")) {
      toast.error("Paste a valid bot token (contains a ':')");
      return;
    }
    setTgBusy(true);
    try {
      const { data } = await api.post("/integrations/telegram/setup", { bot_token: tgToken.trim() });
      const status = await api.get("/integrations/telegram/status");
      setTg(status.data);
      setTgToken("");
      toast.success(`Connected — chat ID ${data.chat_id}` + (data.test_sent ? " · test message sent" : ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed");
    } finally {
      setTgBusy(false);
    }
  };
  const testTelegram = async () => {
    try {
      await api.post("/integrations/telegram/test");
      toast.success("Test message sent — check your phone");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };
  const disconnectTelegram = async () => {
    if (!window.confirm("Disconnect Telegram? You'll stop getting alerts.")) return;
    try {
      await api.post("/integrations/telegram/disconnect");
      const status = await api.get("/integrations/telegram/status");
      setTg(status.data);
      toast.info("Telegram disconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };
  const toggleTgEvent = async (key) => {
    const next = tg.events.includes(key)
      ? tg.events.filter((k) => k !== key)
      : [...tg.events, key];
    try {
      await api.post("/integrations/telegram/setup", { events: next });
      setTg({ ...tg, events: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const numOr = (v, d) => (v === "" || v === null || v === undefined ? d : Number(v));
      const payload = {
        buyer_entity_name: profile.buyer_entity_name || null,
        use_or_assigns: !!profile.use_or_assigns,
        signer_name: profile.signer_name || null,
        signer_title: profile.signer_title || "Authorized Signer",
        signer_phone: profile.signer_phone || null,
        signer_email: profile.signer_email || null,
        signer_address: profile.signer_address || null,
        state: profile.state || "FL",
        default_earnest_money: numOr(profile.default_earnest_money, 100),
        default_inspection_days: numOr(profile.default_inspection_days, 15),
        default_closing_days: numOr(profile.default_closing_days, 30),
        default_wholesale_fee: numOr(profile.default_wholesale_fee, 25000),
        default_investor_profit: numOr(profile.default_investor_profit, 40000),
        default_closing_costs: numOr(profile.default_closing_costs, 15000),
        closing_costs_paid_by: profile.closing_costs_paid_by || "buyer",
        apify_token: profile.apify_token || null,
        apify_actor_id: profile.apify_actor_id || null,
        apify_input_template: profile.apify_input_template || null,
        reapi_key: profile.reapi_key || null,
        signwell_api_key: profile.signwell_api_key || null,
      };
      const { data } = await api.put("/profile", payload);
      setProfile(data);
      toast.success("Profile saved — future contracts will auto-fill");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="animate-spin" size={16} /> Loading profile…
      </div>
    );
  }

  const complete =
    profile.buyer_entity_name &&
    profile.signer_name &&
    profile.signer_phone &&
    profile.signer_email;

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="settings-page">
      <div className="reveal">
        <span className="app-eyebrow">/ Buyer Profile</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">
            Settings
          </h1>
        <p className="text-muted-foreground mt-2">
          Complete your buyer profile once. Every future cash offer PDF will
          auto-fill your entity, signer, and contact info instead of showing
          placeholders like <span className="font-mono text-xs">[Your Name]</span>.
        </p>
      </div>

      {!complete && (
        <div
          className="mt-6 card-flat border-2 border-[hsl(var(--warning))]/40 p-4 flex items-start gap-3"
          data-testid="incomplete-banner"
        >
          <ShieldCheck size={18} className="text-[hsl(var(--warning))] mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold">Profile incomplete</div>
            <div className="text-muted-foreground">
              Fill in your Buyer entity, Signer name, phone, and email so your
              offers look professional and legally identify you as the buyer.
            </div>
          </div>
        </div>
      )}

      {/* SMS Kill Switch — stops every outbound SMS in one click. Voice/email
          stay live. Useful while A2P 10DLC is pending. */}
      <div className={`mt-6 card-flat p-4 flex items-start gap-3 border-2 ${
          smsStatus.sms_paused
            ? "border-rose-300 bg-rose-50/60"
            : "border-emerald-200 bg-emerald-50/40"}`}
           data-testid="sms-kill-switch">
        <div className={`mt-0.5 shrink-0 ${
          smsStatus.sms_paused ? "text-rose-600" : "text-emerald-600"}`}>
          {smsStatus.sms_paused ? <PauseCircle size={22} /> : <PlayCircle size={22} />}
        </div>
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2">
            SMS is currently
            <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded-full ${
              smsStatus.sms_paused
                ? "bg-rose-600 text-white"
                : "bg-emerald-600 text-white"}`}>
              {smsStatus.sms_paused ? "PAUSED" : "LIVE"}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {smsStatus.sms_paused
              ? "All outbound SMS is blocked — drips, AI replies, AI Blast, and SMS Blaster. Voice and email are unaffected. Flip back on when your A2P 10DLC brand is approved."
              : "Every outbound SMS path is live. Each attempted send costs about $0.0079 even when Twilio blocks delivery with error 30034. Pause SMS if you're waiting on A2P 10DLC approval."}
          </div>
        </div>
        <button
          type="button"
          disabled={smsBusy}
          onClick={async () => {
            setSmsBusy(true);
            try {
              const { data } = await api.post("/sms/pause", { paused: !smsStatus.sms_paused });
              setSmsStatus((s) => ({ ...s, sms_paused: data.sms_paused }));
              toast.success(data.sms_paused ? "SMS paused — safe from further charges" : "SMS resumed");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not update SMS pause");
            } finally { setSmsBusy(false); }
          }}
          data-testid="sms-kill-switch-btn"
          className={`shrink-0 rounded-full px-4 py-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50 ${
            smsStatus.sms_paused
              ? "bg-emerald-600 text-white hover:bg-emerald-700"
              : "bg-rose-600 text-white hover:bg-rose-700"}`}
        >
          {smsBusy ? "…" : smsStatus.sms_paused ? "Resume SMS" : "Pause SMS"}
        </button>
      </div>

      {/* Voicemail Kill Switch — mirrors SMS pattern. Blocks every VM-drop
          path: (1) auto-drop during power dial on AMD, (2) manual "Drop VM"
          button, (3) ringless drops from dispositions. */}
      <div className={`mt-4 card-flat p-4 flex items-start gap-3 border-2 ${
          vmStatus.vm_paused
            ? "border-rose-300 bg-rose-50/60"
            : "border-emerald-200 bg-emerald-50/40"}`}
           data-testid="vm-kill-switch">
        <div className={`mt-0.5 shrink-0 ${
          vmStatus.vm_paused ? "text-rose-600" : "text-emerald-600"}`}>
          {vmStatus.vm_paused ? <PauseCircle size={22} /> : <PlayCircle size={22} />}
        </div>
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2">
            Voicemail is currently
            <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded-full ${
              vmStatus.vm_paused
                ? "bg-rose-600 text-white"
                : "bg-emerald-600 text-white"}`}>
              {vmStatus.vm_paused ? "PAUSED" : "LIVE"}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {vmStatus.vm_paused
              ? "Every voicemail-drop path is blocked — auto-drop on AMD during power dial, the manual Drop VM button, and ringless drops from dispositions. Calls and SMS are unaffected."
              : "Voicemail drops are LIVE. Each drop places a fresh outbound call that Twilio bills at ~$0.014/min. Pause to stop leaving voicemails."}
          </div>
        </div>
        <button
          type="button"
          disabled={vmBusy}
          onClick={async () => {
            setVmBusy(true);
            try {
              const { data } = await api.post("/voicemail/pause", { paused: !vmStatus.vm_paused });
              setVmStatus((s) => ({ ...s, vm_paused: data.vm_paused }));
              toast.success(data.vm_paused ? "Voicemails paused" : "Voicemails resumed");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not update voicemail pause");
            } finally { setVmBusy(false); }
          }}
          data-testid="vm-kill-switch-btn"
          className={`shrink-0 rounded-full px-4 py-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50 ${
            vmStatus.vm_paused
              ? "bg-emerald-600 text-white hover:bg-emerald-700"
              : "bg-rose-600 text-white hover:bg-rose-700"}`}
        >
          {vmBusy ? "…" : vmStatus.vm_paused ? "Resume VM" : "Pause VM"}
        </button>
      </div>

      {/* ============ AI LEAD MANAGER — RETELL INBOUND VOICE (Phase 1) ============
          Isolated from the outbound SignalWire dialer. Toggling Voice ON tells
          the inbound SignalWire voice webhook to forward callbacks to the
          Retell PSTN number below; Retell then hits our /api/retell/inbound
          to pull seller context and answers the call. */}
      <div className="mt-6 card-flat p-5 border-2 border-purple-200 bg-gradient-to-br from-purple-50/60 to-white"
           data-testid="ai-lead-manager-card">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0 text-purple-600">
            <PhoneCall size={22} />
          </div>
          <div className="flex-1">
            <div className="font-semibold flex items-center gap-2">
              AI Lead Manager · Voice
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                aiLm.voice_enabled ? "bg-purple-600 text-white" : "bg-slate-300 text-slate-700"
              }`}>
                {aiLm.voice_enabled ? "ON" : "OFF"}
              </span>
              {!aiLm.retell_api_key_configured && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500 text-white"
                      title="Set RETELL_API_KEY in the backend .env / production secrets">
                  API key missing
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              When a seller calls one of your SignalWire numbers, the Retell AI
              agent picks up, identifies the seller by phone, and prequalifies
              them. On seller consent, Retell warm-transfers to the acquisitions
              number below. All transcripts, summaries, and outcomes are auto-
              logged to the lead&apos;s activity feed. The outbound dialer is
              unaffected.
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-xs">Retell Agent ID</Label>
            <Input value={aiLm.retell_agent_id || ""}
                   onChange={(e) => setAiLm({ ...aiLm, retell_agent_id: e.target.value })}
                   onBlur={() => aiLm.retell_agent_id && saveAiLm({ retell_agent_id: aiLm.retell_agent_id })}
                   className="mt-1 h-9 font-mono text-xs"
                   placeholder="agent_..."
                   data-testid="ai-lm-agent-id" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Stored on the tenant. Falls back to RETELL_AGENT_ID env.
            </div>
          </div>
          <div>
            <Label className="text-xs">
              Retell API Key
              {aiLm.retell_api_key_configured
                ? <span className="ml-2 text-emerald-700">✓ configured via env</span>
                : <span className="ml-2 text-amber-700">not configured</span>}
            </Label>
            <Input
              type="text"
              readOnly
              value={aiLm.retell_api_key_configured ? "(stored in RETELL_API_KEY env)" : ""}
              className="mt-1 h-9 font-mono text-xs bg-slate-50"
              placeholder="Set via backend env RETELL_API_KEY only"
              data-testid="ai-lm-retell-api-key"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Secret lives only in the backend env var <code>RETELL_API_KEY</code>. Never persisted to the database. Rotate by updating the env and redeploying.
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Acquisitions Transfer Number</Label>
            <Input
              placeholder="+1XXXXXXXXXX"
              value={aiLm.transfer_number}
              onChange={(e) => setAiLm({ ...aiLm, transfer_number: e.target.value })}
              onBlur={() => aiLm.transfer_number && saveAiLm({ transfer_number: aiLm.transfer_number })}
              className="mt-1 h-9 font-mono text-xs"
              data-testid="ai-lm-transfer-number"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Used by the fallback (Retell unreachable) and by Phase 1.5 warm transfer.
              Change any time — no redeploy required.
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl border border-purple-200 bg-white p-3">
          <div>
            <div className="text-sm font-semibold">Enable AI Voice for inbound callbacks</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Requires: Retell API key + Retell agent ID (both above). Uses SignalWire → Retell SIP —
              no Retell PSTN number needed.
            </div>
          </div>
          <Switch
            checked={aiLm.voice_enabled}
            disabled={aiLmBusy || !aiLm.retell_api_key_configured || !aiLm.retell_agent_id}
            onCheckedChange={(v) => saveAiLm({ voice_enabled: v })}
            data-testid="ai-lm-voice-toggle"
          />
        </div>

        {/* SignalWire Inbound Audit — full Voice + SMS routing view */}
        <SignalWireInboundAudit />
      </div>

      {/* CRM Cleanup — one-click fix for the "COLD tab shows 7000 leads"
          issue caused by historical CSV imports. Moves any lead still in
          the COLD column with no call history back to No Contact. */}
      <div className="mt-4 card-flat p-4 flex items-start gap-3 border border-[var(--app-line)]"
           data-testid="crm-cleanup-cold-card">
        <div className="mt-0.5 shrink-0 text-slate-600">
          <Wrench size={22} />
        </div>
        <div className="flex-1">
          <div className="font-semibold">Rebalance COLD → No Contact</div>
          <div className="text-xs text-muted-foreground mt-1">
            Historical CSV imports dropped fresh leads into the COLD column by
            mistake. This moves every lead sitting in COLD that has never been
            called back into <strong>No Contact</strong>. Safe to run — only
            touches leads with no disposition history.
          </div>
        </div>
        <button
          type="button"
          disabled={reflowBusy}
          onClick={async () => {
            setReflowBusy(true);
            try {
              const { data } = await api.post("/leads/reflow-untouched-cold");
              toast.success(`Moved ${data.moved} lead${data.moved === 1 ? "" : "s"} out of COLD`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Cleanup failed");
            } finally { setReflowBusy(false); }
          }}
          data-testid="reflow-cold-btn"
          className="shrink-0 rounded-full px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50"
        >
          {reflowBusy ? "…" : "Run Cleanup"}
        </button>
      </div>

      {!profile.signer_phone && (
        <div
          className="mt-6 card-flat border-2 border-blue-300 bg-blue-50/40 p-4 flex items-start gap-3"
          data-testid="cell-cta-banner"
        >
          <span className="text-2xl leading-none">📱</span>
          <div className="text-sm flex-1">
            <div className="font-semibold">Save your cell to receive inbound calls</div>
            <div className="text-muted-foreground">
              Enter your mobile number in <strong>Signer phone</strong> below.
              When someone calls your Twilio number and AI Phone is off, we
              auto-forward to your cell so you never miss a lead.
            </div>
          </div>
          <button
            onClick={() => document.getElementById("signer-phone-input")?.focus()}
            className="text-xs px-3 py-1.5 rounded-full border border-blue-400 hover:bg-blue-100 shrink-0"
            data-testid="cell-cta-focus"
          >
            Add now
          </button>
        </div>
      )}

      <div className="card-flat p-6 mt-6 space-y-6 reveal reveal-2">
        <div>
          <div className="font-display text-lg font-bold">Buyer Entity</div>
          <p className="text-xs text-muted-foreground mb-4">
            This appears as the &quot;Buyer&quot; on every contract.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Legal Entity Name</Label>
              <Input
                data-testid="profile-entity"
                className="field-sharp mt-1"
                placeholder="ABC Property Solutions LLC"
                value={profile.buyer_entity_name || ""}
                onChange={(e) => set("buyer_entity_name", e.target.value)}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-3 py-2">
              <Switch
                checked={!!profile.use_or_assigns}
                onCheckedChange={(v) => set("use_or_assigns", v)}
                data-testid="profile-or-assigns"
                id="orAssigns"
              />
              <div>
                <Label htmlFor="orAssigns" className="cursor-pointer">
                  Include &quot;or Assigns&quot; on the Buyer line
                </Label>
                <div className="text-xs text-muted-foreground">
                  Lets you assign the contract to another investor.
                  Common for wholesalers in Florida.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-border">
          <div className="font-display text-lg font-bold pt-4">Signer</div>
          <p className="text-xs text-muted-foreground mb-4">
            The person who signs the contract on behalf of the entity, and
            who the seller can reach.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name</Label>
              <Input
                data-testid="profile-signer-name"
                className="field-sharp mt-1"
                placeholder="John Doe"
                value={profile.signer_name || ""}
                onChange={(e) => set("signer_name", e.target.value)}
              />
            </div>
            <div>
              <Label>Title</Label>
              <Input
                data-testid="profile-signer-title"
                className="field-sharp mt-1"
                placeholder="Managing Member"
                value={profile.signer_title || ""}
                onChange={(e) => set("signer_title", e.target.value)}
              />
            </div>
            <div>
              <Label>Phone <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1">(inbound calls forward here)</span></Label>
              <Input
                id="signer-phone-input"
                data-testid="profile-signer-phone"
                className="field-sharp mt-1"
                placeholder="(305) 555-1234"
                value={profile.signer_phone || ""}
                onChange={(e) => set("signer_phone", e.target.value)}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="profile-signer-email"
                type="email"
                className="field-sharp mt-1"
                placeholder="john@abcps.com"
                value={profile.signer_email || ""}
                onChange={(e) => set("signer_email", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Business Address (optional)</Label>
              <Input
                data-testid="profile-signer-address"
                className="field-sharp mt-1"
                placeholder="123 Ocean Blvd, Miami, FL 33101"
                value={profile.signer_address || ""}
                onChange={(e) => set("signer_address", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-border">
          <div className="font-display text-lg font-bold pt-4">Default Contract Terms</div>
          <p className="text-xs text-muted-foreground mb-4">
            Used to prefill the calculator and populate the purchase agreement.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label>State (Governing Law)</Label>
              <Select
                value={profile.state || "FL"}
                onValueChange={(v) => set("state", v)}
              >
                <SelectTrigger data-testid="profile-state" className="field-sharp mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Closing Costs Paid By</Label>
              <Select
                value={profile.closing_costs_paid_by || "buyer"}
                onValueChange={(v) => set("closing_costs_paid_by", v)}
              >
                <SelectTrigger data-testid="profile-closing-paid-by" className="field-sharp mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer (100%)</SelectItem>
                  <SelectItem value="seller">Seller (100%)</SelectItem>
                  <SelectItem value="split">Split 50/50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Earnest Money $</Label>
              <Input
                data-testid="profile-earnest"
                className="field-sharp mt-1 font-mono"
                value={profile.default_earnest_money ?? ""}
                onChange={(e) => num("default_earnest_money", e.target.value)}
              />
            </div>
            <div>
              <Label>Inspection Days</Label>
              <Input
                data-testid="profile-inspection-days"
                className="field-sharp mt-1 font-mono"
                value={profile.default_inspection_days ?? ""}
                onChange={(e) => num("default_inspection_days", e.target.value)}
              />
            </div>
            <div>
              <Label>Closing Days</Label>
              <Input
                data-testid="profile-closing-days"
                className="field-sharp mt-1 font-mono"
                value={profile.default_closing_days ?? ""}
                onChange={(e) => num("default_closing_days", e.target.value)}
              />
            </div>
            <div>
              <Label>Wholesale Fee $</Label>
              <Input
                data-testid="profile-wholesale-fee"
                className="field-sharp mt-1 font-mono"
                value={profile.default_wholesale_fee ?? ""}
                onChange={(e) => num("default_wholesale_fee", e.target.value)}
              />
            </div>
            <div>
              <Label>Investor Profit $</Label>
              <Input
                data-testid="profile-investor-profit"
                className="field-sharp mt-1 font-mono"
                value={profile.default_investor_profit ?? ""}
                onChange={(e) => num("default_investor_profit", e.target.value)}
              />
            </div>
            <div>
              <Label>Closing Costs $</Label>
              <Input
                data-testid="profile-closing-costs"
                className="field-sharp mt-1 font-mono"
                value={profile.default_closing_costs ?? ""}
                onChange={(e) => num("default_closing_costs", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ============= REALESTATEAPI SKIP TRACE — REMOVED FROM UI (2026-02-19)
            Section removed at user request. The RealEstateAPI service is
            still wired up server-side via the shared FlipPilot key, so
            skip-trace continues to work. Kept the profile.reapi_key DB
            field untouched — if the user re-enables the UI later they
            just paste back into place. */}

        <div className="flex justify-end pt-4 border-t border-border">
          <Button
            onClick={save}
            disabled={busy}
            className="rounded-sm h-11 px-6"
            data-testid="save-profile-button"
          >
            {busy ? (
              <><Loader2 className="animate-spin mr-2" size={14} />Saving…</>
            ) : (
              <><Save size={14} className="mr-2" />Save Profile</>
            )}
          </Button>
        </div>
      </div>

      {/* ============ SignalWire outbound number pool ============
          Admin-only card. Shows every number rotating for outbound calls,
          lets ops buy new ones inline, and force-syncs the pool from
          SignalWire so freshly-bought numbers start dialing within a
          second instead of waiting for the 60s background worker. */}
      {swPool.pool_size > 0 && (
      <div className="card-flat p-6 mt-6 reveal reveal-1" data-testid="signalwire-pool-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <PhoneCall size={20} className="text-orange-600" />
              <div className="font-display text-lg font-bold">Outbound Number Pool</div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 font-semibold" data-testid="sw-pool-count">
                {swPool.pool_size} rotating
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Every outbound call rotates across these numbers using LRU + cooldown + hourly/daily caps to keep any single number under carrier spam thresholds. Buy more to raise your daily-call ceiling (each number ≈ 200 dials/day worry-free).
            </p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={refreshSwPool}
            disabled={swBusy}
            data-testid="sw-pool-refresh-btn"
          >
            <RefreshCw size={13} className={`mr-1.5 ${swBusy ? "animate-spin" : ""}`} />
            Force sync
          </Button>
        </div>

        {/* Buy-more inline form */}
        <div className="mt-5 rounded-xl border border-[var(--app-line)] bg-slate-50 p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag size={15} className="text-emerald-600" />
            <div className="font-semibold text-sm">Buy new numbers</div>
            <span className="text-[10px] text-muted-foreground">~$0.50/mo each · joins the rotation within 60s</span>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Area code</Label>
              <Input
                value={swBuyAreaCode}
                onChange={(e) => setSwBuyAreaCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                placeholder="239"
                className="w-24 mt-1"
                data-testid="sw-buy-area-code"
                maxLength={3}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">How many</Label>
              <Input
                type="number" min={1} max={10}
                value={swBuyCount}
                onChange={(e) => setSwBuyCount(Number(e.target.value))}
                className="w-20 mt-1"
                data-testid="sw-buy-count"
              />
            </div>
            <Button
              onClick={buySwNumbers}
              disabled={swBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sw-buy-btn"
            >
              {swBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ShoppingBag size={14} className="mr-1.5" />}
              Buy {swBuyCount} number{swBuyCount === 1 ? "" : "s"}
            </Button>
          </div>
        </div>

        {/* Number heatmap — first 30 numbers with hour/day usage */}
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Live usage (per number, resets hourly + daily)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="sw-pool-heatmap">
            {(swPool.numbers || []).slice(0, 30).map((n) => {
              const hour = n.count_hour || 0;
              const day  = n.count_day  || 0;
              const hot  = hour >= 40 || day >= 160;   // 80% of caps
              const warm = hour >= 20 || day >= 80;    // 40% of caps
              const tone = hot ? "bg-red-50 border-red-300 text-red-800"
                        : warm ? "bg-amber-50 border-amber-300 text-amber-800"
                        :        "bg-emerald-50 border-emerald-200 text-emerald-800";
              return (
                <div key={n.phone}
                     className={`rounded-lg border px-2.5 py-1.5 text-[11px] flex items-center justify-between ${tone}`}
                     data-testid={`sw-pool-num-${n.phone.replace(/[^0-9]/g,'')}`}>
                  <span className="font-mono">{n.phone}</span>
                  <span className="tabular-nums">{hour}<span className="opacity-60">h</span>·{day}<span className="opacity-60">d</span></span>
                </div>
              );
            })}
          </div>
          {(swPool.numbers || []).length > 30 && (
            <div className="text-[10px] text-muted-foreground mt-2">+{swPool.numbers.length - 30} more not shown</div>
          )}
        </div>
      </div>
      )}

      <div className="card-flat p-6 mt-6 reveal reveal-2" data-testid="telegram-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <MessageSquare size={20} className="text-sky-600" />
              <div className="font-display text-lg font-bold">Telegram Reports</div>
              {tg.configured && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                  ✓ Connected
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Get real-time alerts on your phone the moment a lead responds, a call is dispositioned,
              or an offer is signed — pushed via your private Telegram bot.
            </p>
          </div>
        </div>

        {!tg.configured ? (
          <div className="mt-4 space-y-3">
            <div className="text-xs text-muted-foreground bg-sky-50 border border-sky-200 rounded-lg p-3 leading-relaxed">
              <b>Setup — 2 min, free</b>
              <ol className="mt-2 space-y-1 list-decimal list-inside">
                <li>Open Telegram → message <b>@BotFather</b> → send <code>/newbot</code> → copy the bot token</li>
                <li><b>Open your new bot and send it any message (e.g. &quot;hi&quot;)</b> — this creates the chat so we can find you</li>
                <li>Paste the bot token below and hit Connect — we&apos;ll auto-detect your chat ID and send a test alert</li>
              </ol>
            </div>
            <div>
              <Label>Bot token</Label>
              <Input
                type="text"
                value={tgToken}
                onChange={(e) => setTgToken(e.target.value)}
                placeholder="123456789:AAExampleTokenFromBotFather"
                className="field-sharp mt-1 font-mono text-xs"
                data-testid="tg-token-input"
              />
            </div>
            <Button
              onClick={setupTelegram}
              disabled={tgBusy || !tgToken.trim()}
              className="rounded-sm"
              data-testid="tg-setup-btn"
            >
              {tgBusy
                ? <><Loader2 className="animate-spin mr-2" size={13} /> Connecting…</>
                : <><MessageSquare size={13} className="mr-2" /> Connect Telegram</>}
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <b>Connected</b> · Chat ID <code className="ml-1 font-mono">{tg.chat_id}</code>
            </div>
            <div>
              <Label>Which events send alerts?</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { key: "new_lead",     label: "New Lead", emoji: "🆕" },
                  { key: "hot_lead",     label: "Hot Lead", emoji: "🔥" },
                  { key: "inbound_sms",  label: "Inbound SMS", emoji: "💬" },
                  { key: "missed_call",  label: "Missed Call", emoji: "📵" },
                  { key: "call_dispo",   label: "Call Disposition", emoji: "📞" },
                  { key: "offer_sent",   label: "Offer Sent", emoji: "📄" },
                  { key: "offer_signed", label: "Offer Signed", emoji: "🎉" },
                  { key: "daily_digest", label: "Daily Digest (8pm)", emoji: "📊" },
                ].map((ev) => {
                  const active = tg.events.includes(ev.key);
                  return (
                    <button
                      key={ev.key}
                      onClick={() => toggleTgEvent(ev.key)}
                      className={`text-left text-xs px-3 py-2 rounded-lg border transition ${
                        active
                          ? "bg-sky-50 border-sky-300 text-sky-900"
                          : "bg-white border-[var(--app-line)] text-muted-foreground hover:bg-[var(--app-surface)]"
                      }`}
                      data-testid={`tg-event-${ev.key}`}
                    >
                      <span className="mr-1">{ev.emoji}</span>
                      {ev.label}
                      {active && <span className="ml-1 text-emerald-600 font-bold">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={testTelegram}
                variant="outline"
                className="rounded-sm"
                data-testid="tg-test-btn"
              >
                <Send size={13} className="mr-2" /> Send test alert
              </Button>
              <Button
                onClick={disconnectTelegram}
                variant="outline"
                className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                data-testid="tg-disconnect-btn"
              >
                <X size={13} className="mr-2" /> Disconnect
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="card-flat p-6 mt-6 reveal reveal-3" data-testid="signwell-card" hidden style={{display:'none'}}>
        {/* SignWell card removed from UI per user request (2026-02-19). The
            shared FlipPilot SignWell key keeps working server-side, so
            e-signature flows on the Offers page continue as-is. This
            hidden shell preserves the surrounding reveal-N stagger
            indices — safe to fully delete in a future refactor. */}
      </div>

      <div className="card-flat p-6 mt-6 reveal reveal-3" data-testid="docusign-card" hidden style={{display:'none'}}>
        {/* DocuSign card removed per user request (2026-02-19). SignWell is the
            sole e-signature provider; this hidden shell is retained temporarily
            to avoid disturbing surrounding reveal-N stagger indices — the
            div renders nothing and imposes no cost. Safe to fully delete
            during the next Settings refactor. */}
      </div>

      <DialerSettingsCard />
      <InboundBillingAudit />
      <LeadWebhookCard />
      <AutomationCard />
    </div>
  );
}

function LeadWebhookCard() {
  const [cfg, setCfg] = useState(null);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    try {
      const { data } = await api.get("/lead-webhook/settings");
      setCfg(data);
      setPhone(data.alert_phone || "");
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return null;
  const fullUrl = `${base}${cfg.url}`;

  const copy = () => { navigator.clipboard.writeText(fullUrl); toast.success("Webhook URL copied"); };
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/lead-webhook/settings", {
        alert_phone: phone,
        notify_telegram: cfg.notify_telegram,
        notify_sms: cfg.notify_sms,
      });
      toast.success("Alert settings saved");
      load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const regen = async () => {
    if (!window.confirm("Generate a new URL? The old one stops working immediately.")) return;
    try {
      const { data } = await api.post("/lead-webhook/regenerate");
      setCfg((c) => ({ ...c, url: data.url, token: data.token }));
      toast.success("New webhook URL generated");
    } catch (e) { toast.error("Could not regenerate"); }
  };

  const sample = `{
  "name": "Jane Seller",
  "phone": "+1 305 555 0142",
  "email": "jane@email.com",
  "property_address": "123 Main St, Miami, FL 33101",
  "message": "Interested in a cash offer",
  "source": "Facebook Form"
}`;

  return (
    <div className="card-flat p-6 mt-6" data-testid="lead-webhook-card">
      <div className="flex items-center gap-2 mb-1">
        <Link2 size={18} />
        <div className="font-display text-lg font-bold">Lead Capture Webhook</div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Paste this URL into any lead source (Zapier, Make, a website form, or
        Facebook Lead Ads via Zapier). Every lead drops straight into your
        <b> 📥 Property Leads</b> column and pings you instantly.
      </p>

      <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Your webhook URL</Label>
      <div className="flex gap-2 mt-1">
        <Input readOnly value={fullUrl} className="font-mono text-xs" data-testid="lead-webhook-url" />
        <Button variant="outline" onClick={copy} data-testid="lead-webhook-copy"><Copy size={14} className="mr-1" /> Copy</Button>
        <Button variant="outline" onClick={regen} title="Generate a new URL" data-testid="lead-webhook-regen"><RefreshCw size={14} /></Button>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Text alerts to this phone</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 305 555 0199" className="mt-1" data-testid="lead-webhook-phone" />
          <p className="text-[11px] text-muted-foreground mt-1">Texted the instant a lead arrives (uses your SMS credits).</p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm">Telegram alert</div>
            <Switch checked={!!cfg.notify_telegram} onCheckedChange={(v) => setCfg({ ...cfg, notify_telegram: v })} data-testid="lead-webhook-tg" />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm">Text-message alert</div>
            <Switch checked={!!cfg.notify_sms} onCheckedChange={(v) => setCfg({ ...cfg, notify_sms: v })} data-testid="lead-webhook-sms" />
          </div>
          <div className="flex items-center justify-between opacity-70">
            <div className="text-sm">In-app bell</div>
            <Switch checked disabled />
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="mt-4 rounded-full" data-testid="lead-webhook-save">
        {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
        Save alerts
      </Button>

      <div className="mt-5">
        <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Sample payload (JSON or form fields)</Label>
        <pre className="text-[11px] bg-[var(--app-surface)] rounded-md p-3 mt-1 overflow-auto">{sample}</pre>
        <p className="text-[11px] text-muted-foreground mt-1">
          Flexible field names accepted: name / full_name / first_name+last_name,
          phone / mobile / cell, email, property_address / address / street,
          message / notes, source.
        </p>
      </div>
    </div>
  );
}

function AutomationCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/automation/settings")
      .then((r) => setS(r.data))
      .catch(() => {});
  }, []);

  const toggle = async (key, val) => {
    setBusy(true);
    try {
      const { data } = await api.put("/automation/settings", { [key]: val });
      setS(data);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  if (!s) return null;

  const rows = [
    {
      key: "drip_enabled",
      title: "Auto Follow-Up Drips",
      desc: "When a lead moves to 'Offer Sent', auto-schedule day-3, day-7 and day-30 follow-ups (email if available, else SMS).",
    },
    {
      key: "round_robin_enabled",
      title: "Round-Robin Auto-Assign",
      desc: "New leads (manual, sync, or Lead Finder) get distributed across your active teammates automatically.",
    },
    {
      key: "sms_over_email_when_possible",
      title: "Prefer SMS Over Email",
      desc: "When a drip fires and the lead has both an email and phone, send an SMS instead. Higher open-rate but uses Twilio credits.",
    },
  ];

  return (
    <div className="card-flat p-6 mt-6 reveal reveal-4" data-testid="automation-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-lg font-bold">AI Automation</div>
          <p className="text-xs text-muted-foreground mt-1">
            Let FlipPilot follow up, assign, and recommend the next action on
            every lead — so nothing goes cold.
          </p>
        </div>
      </div>
      <div className="space-y-4">
        {rows.map((r) => (
          <div
            key={r.key}
            className="flex items-start justify-between gap-4 py-2 border-b border-[var(--app-line)] last:border-b-0"
            data-testid={`automation-row-${r.key}`}
          >
            <div className="flex-1">
              <div className="text-sm font-medium">{r.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{r.desc}</div>
            </div>
            <Switch
              checked={!!s[r.key]}
              onCheckedChange={(v) => toggle(r.key, v)}
              disabled={busy}
              data-testid={`automation-toggle-${r.key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
